/**
 * useABTestV2 - シンプル版のABテスト用ユーティリティ関数 V2
 *
 * このユーティリティは設定に基づいてA/Bパターンを決定し、結果を返します。
 * ユーザーのCookieに基づいて一貫性のあるパターン割り当てを行います。
 *
 * ■ A/Bテストの基本フロー
 * 1. テスト設定・コード実装
 * 2. リリースでテスト開始
 * 3. 終了依頼があったとき、テストを無効して、リリースする。
 *
 * ■ 使用方法
 *
 * utils/abtest/abTestConfig.jsにABテストの設定を記述し、
 * 例：
 * ```javascript
 * export const abTestConfig = {
 *   testName: 'button_color_test',  // テスト名（Cookie名に使用）
 *   bPatternRatio: 50,              // Bパターンの割合（0-100）
 *   enable: true                    // テストの有効/無効、最初はtrue、リリースするとテスト開始になる。
 * }
 * ```
 *
 * 次に、コンポーネント内でuseABTestV2をインポートして使用します。
 * ```javascript
 * import useABTestV2 from '@/utils/abtest/useABTestV2'
 *
 * // パターン判定実行
 * const { isAPattern, isBPattern } = useABTestV2(testConfig)
 *
 * // テスト有効であるかどうかを確認
 * if (isAPattern || isBPattern) {
 * 　　// テスト実行中
 * }
 * // A/Bパターンに応じた処理を実行
 * if (isAPattern) {
 * 　　// Aパターンの処理
 * } else if (isBPattern) {
 * 　　// Bパターンの処理
 * }
 * ```
 *　ABテストを終了したい場合は、テスト設定のenableをfalseに変更して、リリースする。
 * 例：
 * export const abTestConfig = {
 *   testName: 'button_color_test',  // テスト名（Cookie名に使用）
 *   bPatternRatio: 50,              // Bパターンの割合（0-100）
 *   enable: flase                   // テストの無効。
 * }
 */

/**
 * シンプル版のABテスト実行関数 V2
 */
export default function useABTestV2(config) {
  // 結果オブジェクト初期化
  const result = {
    isAPattern: false,
    isBPattern: false
  }

  // ABテストが無効なら初期値を返す
  if (!config.enable) {
    return result
  }

  const abUidCookieName = `${config.testName.toLowerCase()}_ab_uid`
  const cookieValue = useCookie(abUidCookieName, {
    maxAge: 60 * 60 * 24 * 365 // 1年間
  })

  let randomNumber
  randomNumber = cookieValue.value ? Number(cookieValue.value) : undefined

  // Cookieが未設定の場合は設定する
  if (randomNumber === undefined) {
    randomNumber = Math.floor(Math.random() * 100)
    cookieValue.value = String(randomNumber)

    // Log cookie write in test environment
    if (!isProduction()) {
      console.log(`[AB Test] Cookie written: ${abUidCookieName} = ${randomNumber}`)
    }
  }

  const normalizedRandom = randomNumber % 100

  /**
   * bPatternRangeが指定されていればそれを優先し、なければbPatternRatioでB群判定
   * bPatternRange: [start, end] の範囲（両端含む）でB群
   * bPatternRatio: 0-100の割合でB群（従来通り）
   */
  if (config.bPatternRange) {
    const [start, end] = config.bPatternRange
    result.isBPattern = normalizedRandom >= start && normalizedRandom <= end
  } else {
    result.isBPattern = normalizedRandom < config.bPatternRatio
  }

  result.isAPattern = !result.isBPattern

  return result
}
